'use client';

import { Fragment, useEffect, useState } from 'react';
import { DirectionProvider } from '@base-ui/react/direction-provider';
import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import { IntlProvider } from 'react-intl';
import { loadLocaleMessages } from '../messages';
import { DocumentLanguage } from './document-language';
import {
  type LanguageProviderProps,
  defaultRichTextElements,
  textDirection,
} from './shared';

/**
 * Holds `react-intl` for one locale's messages.
 *
 * The server resolves the set for the locale the tree renders in and passes it
 * as `initialMessages`, so the server render is already translated and the
 * browser has nothing to wait for. When the server sends a set for a new
 * locale, as `router.refresh()` does after Settings → Language, that set
 * replaces the active one. A `locale` with no server set loads its own.
 */
export function LanguageProvider({
  children,
  locale,
  initialMessages,
  setsDocumentLanguage = false,
  timeZone,
}: LanguageProviderProps) {
  const [active, setActive] = useState(initialMessages);
  // Keyed on the locale, not the object: callers build `initialMessages`
  // inline, so its identity changes on every render.
  const [seededLocale, setSeededLocale] = useState(initialMessages.locale);
  if (initialMessages.locale !== seededLocale) {
    setSeededLocale(initialMessages.locale);
    if (initialMessages.locale === locale) {
      setActive(initialMessages);
    }
  }

  useEffect(() => {
    if (locale === active.locale) {
      return;
    }

    let cancelled = false;
    loadLocaleMessages(locale)
      .then((loaded) => {
        if (!cancelled) {
          setActive(loaded);
        }
      })
      .catch((error) => {
        console.error(`[i18n] Failed to load messages for ${locale}`, error);
      });

    return () => {
      cancelled = true;
    };
  }, [locale, active.locale]);

  function handleError(error: Error & { code: string }) {
    if (
      process.env.NEXT_PUBLIC_I18N_LOG_MISSING_TRANSLATIONS === 'true' &&
      error.code === 'MISSING_TRANSLATION'
    ) {
      console.warn(error.message);
    }
  }

  // Keyed on the locale whose messages are actually in hand: rendering a new
  // `locale` against the previous messages would blank every string for the
  // tick the new set takes to load. Base UI, and so Ripple, and shadcn's Radix
  // components each read direction from their own provider rather than
  // `<html dir>`, for arrow keys and placement.
  const direction = textDirection(active.locale);
  return (
    <DirectionProvider direction={direction}>
      <RadixDirectionProvider dir={direction}>
        <IntlProvider
          locale={active.locale}
          key={active.locale}
          messages={active.messages}
          textComponent={Fragment}
          defaultRichTextElements={defaultRichTextElements}
          timeZone={timeZone}
          onError={handleError}
        >
          {setsDocumentLanguage && <DocumentLanguage locale={active.locale} />}
          {children}
        </IntlProvider>
      </RadixDirectionProvider>
    </DirectionProvider>
  );
}
